import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import { useNavigate, useParams } from "react-router-dom";
import ReactFlow, {
  Background, Controls, MiniMap, addEdge, useNodesState, useEdgesState,
  Handle, Position, ReactFlowProvider, useReactFlow,
} from "reactflow";
import "reactflow/dist/style.css";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import {
  ChevronLeft, Save, Rocket, TestTube2, Trash2, Plus,
  Play, MessageSquareText, MousePointerClick, HelpCircle, GitBranch,
  Tag, TagsIcon, UserPen, Clock, UserRoundCog, Home, Square, AlertCircle,
  MessageCircle, Bot, X, Send,
} from "lucide-react";
import { toast } from "sonner";

// ─────────────────── Node type registry (palette + node component) ───────────────────
const NODE_TYPES = [
  { key: "trigger",         label: "Trigger",          Icon: Play,             color: "border-orange-500 bg-orange-50 dark:bg-orange-950/30", accent: "bg-orange-500", unique: true },
  { key: "send_text",       label: "Send Text",        Icon: MessageSquareText,color: "border-sky-500 bg-sky-50 dark:bg-sky-950/30",           accent: "bg-sky-500" },
  { key: "send_buttons",    label: "Send Buttons",     Icon: MousePointerClick,color: "border-fuchsia-500 bg-fuchsia-50 dark:bg-fuchsia-950/30",accent: "bg-fuchsia-500" },
  { key: "ask_question",    label: "Ask Question",     Icon: HelpCircle,       color: "border-indigo-500 bg-indigo-50 dark:bg-indigo-950/30",  accent: "bg-indigo-500" },
  { key: "condition",       label: "Condition",        Icon: GitBranch,        color: "border-amber-500 bg-amber-50 dark:bg-amber-950/30",     accent: "bg-amber-500" },
  { key: "assign_tag",      label: "Assign Tag",       Icon: Tag,              color: "border-emerald-500 bg-emerald-50 dark:bg-emerald-950/30",accent: "bg-emerald-500" },
  { key: "remove_tag",      label: "Remove Tag",       Icon: TagsIcon,         color: "border-lime-500 bg-lime-50 dark:bg-lime-950/30",         accent: "bg-lime-500" },
  { key: "update_contact",  label: "Update Contact",   Icon: UserPen,          color: "border-cyan-500 bg-cyan-50 dark:bg-cyan-950/30",         accent: "bg-cyan-500" },
  { key: "delay",           label: "Delay",            Icon: Clock,            color: "border-slate-500 bg-slate-50 dark:bg-slate-950/30",     accent: "bg-slate-500" },
  { key: "human_handover",  label: "Human Handover",   Icon: UserRoundCog,     color: "border-rose-500 bg-rose-50 dark:bg-rose-950/30",         accent: "bg-rose-500" },
  { key: "goto_main",       label: "Go to Main Menu",  Icon: Home,             color: "border-purple-500 bg-purple-50 dark:bg-purple-950/30",   accent: "bg-purple-500" },
  { key: "end_chat",        label: "End Chat",         Icon: Square,           color: "border-zinc-500 bg-zinc-50 dark:bg-zinc-950/30",         accent: "bg-zinc-500" },
];
const nodeMeta = (t) => NODE_TYPES.find(n => n.key === t) || NODE_TYPES[1];

// ─────────────────── Custom node component ───────────────────
function BotNode({ id, data, selected }) {
  const meta = nodeMeta(data.type);
  const Icon = meta.Icon;
  const isCondition = data.type === "condition";
  const isButtons = data.type === "send_buttons";
  const isTrigger = data.type === "trigger";
  const buttons = data.data?.buttons || [];
  const preview = summariseNode(data.type, data.data || {});

  return (
    <div
      className={`rounded-sm border-2 shadow-sm w-64 bg-background text-foreground overflow-hidden ${meta.color} ${selected ? "ring-2 ring-orange-400" : ""}`}
      data-testid={`bot-node-${id}`}
    >
      {!isTrigger && (
        <Handle type="target" position={Position.Left}
                className="!w-2 !h-2 !bg-slate-500" />
      )}
      <div className={`h-1 ${meta.accent}`} />
      <div className="px-2.5 py-2 space-y-1">
        <div className="flex items-center gap-1.5">
          <Icon className="h-3.5 w-3.5" />
          <div className="text-[11px] font-bold uppercase tracking-wider">{meta.label}</div>
        </div>
        <div className="text-[11px] text-muted-foreground line-clamp-3 min-h-[16px]">
          {preview || <span className="italic">Not configured</span>}
        </div>
        {isButtons && buttons.length > 0 && (
          <div className="space-y-0.5 pt-1">
            {buttons.map((b, i) => (
              <div key={b.id || i} className="relative border rounded-sm bg-background/80 text-[10px] px-1.5 py-1 flex items-center justify-between">
                <span className="truncate">{b.title || `Button ${i+1}`}</span>
                <Handle
                  id={b.id || `btn_${i}`}
                  type="source"
                  position={Position.Right}
                  style={{ top: "50%" }}
                  className="!w-2 !h-2 !bg-fuchsia-500"
                />
              </div>
            ))}
          </div>
        )}
      </div>
      {/* Default source handle for non-branching nodes */}
      {!isButtons && !isCondition && (
        <Handle
          id="next"
          type="source"
          position={Position.Right}
          className="!w-2 !h-2 !bg-emerald-500"
        />
      )}
      {isCondition && (
        <>
          <div className="grid grid-cols-2 border-t text-[10px] font-semibold">
            <div className="relative py-1 text-center text-emerald-600 border-r">
              True
              <Handle id="true" type="source" position={Position.Bottom} style={{ left: "25%" }} className="!w-2 !h-2 !bg-emerald-500" />
            </div>
            <div className="relative py-1 text-center text-red-600">
              False
              <Handle id="false" type="source" position={Position.Bottom} style={{ left: "75%" }} className="!w-2 !h-2 !bg-red-500" />
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function summariseNode(type, data) {
  switch (type) {
    case "trigger":         return `Triggers: ${data.trigger_type || "any_message"}${data.keywords?.length ? " · " + data.keywords.join(", ") : ""}`;
    case "send_text":       return data.text || "";
    case "send_buttons":    return data.body_text || "";
    case "ask_question":    return `${data.question || ""} → {{custom.${data.response_variable || "answer"}}}`;
    case "condition":       return `${data.variable || "?"} ${data.operator || "equals"} ${data.value || "?"}`;
    case "assign_tag":      return `Add tag: ${data.tag || "?"}`;
    case "remove_tag":      return `Remove tag: ${data.tag || "?"}`;
    case "update_contact":  { const upd = data.updates || {}; return Object.keys(upd).length ? `Update ${Object.keys(upd).join(", ")}` : "Not configured"; }
    case "delay":           return `Wait ${data.seconds || 0}s`;
    case "human_handover":  return data.message || "Hand over to team";
    case "goto_main":       return "Loop back to start";
    case "end_chat":        return data.text || "End session";
    default: return "";
  }
}

const nodeTypes = { bot: BotNode };

// ─────────────────── Main Builder Component ───────────────────
export default function ChatbotBuilder() {
  return (
    <ReactFlowProvider>
      <InnerBuilder />
    </ReactFlowProvider>
  );
}

function InnerBuilder() {
  const { id: botId } = useParams();
  const nav = useNavigate();
  const [bot, setBot] = useState(null);
  const [nodes, setNodes, onNodesChange] = useNodesState([]);
  const [edges, setEdges, onEdgesChange] = useEdgesState([]);
  const [selected, setSelected] = useState(null);
  const [saving, setSaving] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [validation, setValidation] = useState(null);
  const [testOpen, setTestOpen] = useState(false);
  const [testMsgs, setTestMsgs] = useState([]);
  const [testInput, setTestInput] = useState("Hi");
  const flowInstance = useReactFlow();
  const containerRef = useRef(null);

  // ─── Load bot + draft
  useEffect(() => {
    if (!botId) return;
    (async () => {
      try {
        const [b, d] = await Promise.all([
          api.get(`/chatbots/${botId}`),
          api.get(`/chatbots/${botId}/draft`),
        ]);
        setBot(b.data);
        const rfNodes = (d.data.nodes || []).map(n => ({
          id: n.id,
          type: "bot",
          position: n.position || { x: 100, y: 100 },
          data: { type: n.type, data: n.data || {} },
        }));
        const rfEdges = (d.data.edges || []).map(e => ({
          id: e.id,
          source: e.source, target: e.target,
          sourceHandle: e.source_handle || null,
          style: e.source_handle === "false" ? { stroke: "#ef4444" } :
                 e.source_handle === "true"  ? { stroke: "#10b981" } : undefined,
          type: "smoothstep",
        }));
        setNodes(rfNodes);
        setEdges(rfEdges);
      } catch (e) {
        toast.error("Failed to load chatbot");
        nav("/chatbots");
      }
    })();
  }, [botId, nav, setNodes, setEdges]);

  // ─── Warn on unsaved changes
  useEffect(() => {
    const handler = (e) => { if (dirty) { e.preventDefault(); e.returnValue = ""; } };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [dirty]);

  const markDirty = useCallback(() => setDirty(true), []);
  useEffect(() => { markDirty(); }, [nodes, edges, markDirty]);

  // ─── Add node from palette
  const addNode = (typeKey) => {
    // Enforce trigger uniqueness
    if (typeKey === "trigger" && nodes.some(n => n.data.type === "trigger")) {
      toast.error("Only one trigger node is allowed.");
      return;
    }
    const id = "n" + Math.random().toString(36).slice(2, 10);
    const defaultData = defaultsFor(typeKey);
    const center = flowInstance.project?.({ x: (containerRef.current?.clientWidth || 900) / 2 - 130, y: 200 }) || { x: 300, y: 200 };
    const newNode = {
      id, type: "bot",
      position: { x: center.x + Math.random() * 40, y: center.y + Math.random() * 40 },
      data: { type: typeKey, data: defaultData },
    };
    setNodes(ns => [...ns, newNode]);
    setSelected(id);
  };

  // ─── Connect edges
  const onConnect = useCallback((params) => {
    const src = nodes.find(n => n.id === params.source);
    // For condition/button nodes, sourceHandle carries meaning
    const style = params.sourceHandle === "false" ? { stroke: "#ef4444" } :
                  params.sourceHandle === "true"  ? { stroke: "#10b981" } : undefined;
    setEdges(es => addEdge({ ...params, id: "e"+Math.random().toString(36).slice(2,8), type: "smoothstep", style }, es));
  }, [nodes, setEdges]);

  // ─── Delete selected
  const deleteSelected = () => {
    if (!selected) return;
    setNodes(ns => ns.filter(n => n.id !== selected));
    setEdges(es => es.filter(e => e.source !== selected && e.target !== selected));
    setSelected(null);
  };

  // ─── Save draft
  const buildPayload = () => {
    return {
      nodes: nodes.map(n => ({
        id: n.id, type: n.data.type,
        position: { x: n.position.x, y: n.position.y },
        data: n.data.data || {},
      })),
      edges: edges.map(e => ({
        id: e.id, source: e.source, target: e.target,
        source_handle: e.sourceHandle || null,
      })),
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/chatbots/${botId}/draft`, buildPayload());
      toast.success("Draft saved");
      setDirty(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const validate = async () => {
    try {
      await save();
      const { data } = await api.post(`/chatbots/${botId}/validate`);
      setValidation(data);
      if (data.ok) toast.success("Flow is valid — ready to publish");
      else toast.error(`${data.errors.length} validation issue(s)`);
    } catch (e) { toast.error("Validation failed"); }
  };

  const publish = async () => {
    setPublishing(true);
    try {
      await save();
      await api.post(`/chatbots/${botId}/publish`);
      toast.success("Chatbot published — live!");
      setValidation({ ok: true, errors: [] });
      const b = await api.get(`/chatbots/${botId}`);
      setBot(b.data);
    } catch (e) {
      const err = e.response?.data?.detail;
      const errs = err?.errors || [];
      setValidation({ ok: false, errors: errs });
      toast.error(errs.length ? `Cannot publish: ${errs[0]}` : "Publish failed");
    } finally { setPublishing(false); }
  };

  const doTest = async () => {
    try {
      await save();
      const { data } = await api.post(`/chatbots/${botId}/test`, { message: testInput || "Hi" });
      setTestMsgs(data.messages || []);
    } catch (e) { toast.error(e.response?.data?.detail || "Test failed"); }
  };

  // ─── Update selected node data
  const selectedNode = useMemo(() => nodes.find(n => n.id === selected) || null, [selected, nodes]);
  const updateSelectedData = (patch) => {
    setNodes(ns => ns.map(n => n.id === selected ? { ...n, data: { ...n.data, data: { ...(n.data.data || {}), ...patch } } } : n));
  };

  return (
    <div className="flex flex-col h-[calc(100vh-4rem)]" data-testid="bot-builder">
      {/* Header */}
      <div className="border-b p-3 flex items-center gap-3 bg-background/80 backdrop-blur">
        <Button variant="ghost" size="sm" onClick={() => { if (dirty && !window.confirm("Discard unsaved changes?")) return; nav("/chatbots"); }} className="h-8 gap-1" data-testid="back-to-chatbots">
          <ChevronLeft className="h-4 w-4" /> All Chatbots
        </Button>
        <Separator orientation="vertical" className="h-6" />
        <div className="flex items-center gap-2 min-w-0 flex-1">
          <div className="h-8 w-8 rounded-sm bg-gradient-to-br from-orange-400 to-amber-600 text-white flex items-center justify-center">
            <Bot className="h-4 w-4" />
          </div>
          <div className="min-w-0">
            <div className="text-sm font-bold truncate" data-testid="builder-bot-name">{bot?.name || "Loading…"}</div>
            <div className="text-[10px] text-muted-foreground flex items-center gap-2">
              <span className={`inline-block h-1.5 w-1.5 rounded-full ${bot?.status === "published" ? "bg-emerald-500" : "bg-slate-400"}`} />
              {bot?.status?.toUpperCase()} · {nodes.length} nodes · {edges.length} edges
              {dirty && <span className="text-amber-600 font-semibold">· Unsaved</span>}
            </div>
          </div>
        </div>
        <Button variant="outline" size="sm" onClick={validate} className="rounded-sm h-8 gap-1" data-testid="validate-btn">
          <AlertCircle className="h-3.5 w-3.5" /> Validate
        </Button>
        <Button variant="outline" size="sm" onClick={() => { setTestMsgs([]); setTestOpen(true); }} className="rounded-sm h-8 gap-1" data-testid="test-btn">
          <TestTube2 className="h-3.5 w-3.5" /> Test
        </Button>
        <Button variant="outline" size="sm" onClick={save} disabled={saving} className="rounded-sm h-8 gap-1" data-testid="save-btn">
          <Save className="h-3.5 w-3.5" /> {saving ? "Saving…" : "Save"}
        </Button>
        <Button onClick={publish} disabled={publishing} className="rounded-sm h-8 gap-1 bg-orange-500 hover:bg-orange-600 text-white" data-testid="publish-btn">
          <Rocket className="h-3.5 w-3.5" /> {publishing ? "Publishing…" : "Publish"}
        </Button>
      </div>

      <div className="flex-1 flex overflow-hidden">
        {/* Left: node palette */}
        <aside className="w-52 border-r bg-muted/20 p-2 overflow-y-auto shrink-0" data-testid="node-palette">
          <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground p-1 mb-1">Nodes</div>
          <div className="space-y-1">
            {NODE_TYPES.map(t => (
              <button
                key={t.key}
                onClick={() => addNode(t.key)}
                className="w-full flex items-center gap-2 text-left p-2 rounded-sm border bg-background hover:border-orange-400 hover:bg-orange-50 dark:hover:bg-orange-950/30 transition text-xs"
                data-testid={`palette-${t.key}`}
                title={t.unique ? "Only one allowed per bot" : ""}
              >
                <t.Icon className="h-3.5 w-3.5 shrink-0" />
                <span className="truncate">{t.label}</span>
                <Plus className="h-3 w-3 ml-auto text-muted-foreground shrink-0" />
              </button>
            ))}
          </div>
          {validation && (
            <div className="mt-3 p-2 border rounded-sm text-[11px] space-y-1" data-testid="validation-summary">
              <div className={`font-semibold ${validation.ok ? "text-emerald-600" : "text-red-600"}`}>
                {validation.ok ? "✓ Valid" : `${validation.errors.length} issue(s)`}
              </div>
              {validation.errors.slice(0, 8).map((err, i) => (
                <div key={i} className="text-muted-foreground text-[10px]">• {err}</div>
              ))}
            </div>
          )}
        </aside>

        {/* Canvas */}
        <div className="flex-1 relative" ref={containerRef}>
          <ReactFlow
            nodes={nodes}
            edges={edges}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={(_, node) => setSelected(node.id)}
            onPaneClick={() => setSelected(null)}
            nodeTypes={nodeTypes}
            fitView
            snapToGrid
            snapGrid={[10, 10]}
            deleteKeyCode={["Backspace", "Delete"]}
          >
            <Background gap={16} color="#94a3b880" />
            <Controls className="!bg-background !border !rounded-sm" />
            <MiniMap className="!bg-background !border !rounded-sm" pannable zoomable />
          </ReactFlow>
        </div>

        {/* Right: properties panel */}
        <aside className="w-72 border-l bg-muted/20 overflow-y-auto shrink-0" data-testid="props-panel">
          {selectedNode ? (
            <NodeProperties node={selectedNode} onChange={updateSelectedData} onDelete={deleteSelected} />
          ) : (
            <div className="p-4 text-xs text-muted-foreground text-center space-y-2 mt-8">
              <MessageCircle className="h-8 w-8 mx-auto text-muted-foreground/30" />
              <div>Click any node on the canvas to edit its properties.</div>
              <div className="pt-2 text-[10px] italic">Or use the left palette to add a new node.</div>
            </div>
          )}
        </aside>
      </div>

      {/* Test dialog */}
      <Dialog open={testOpen} onOpenChange={setTestOpen}>
        <DialogContent className="max-w-lg rounded-sm">
          <DialogHeader>
            <DialogTitle className="text-lg font-black tracking-tight flex items-center gap-2">
              <TestTube2 className="h-4 w-4" /> Test Chatbot (Preview)
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-2">
            <div className="text-xs text-muted-foreground">
              Simulates a customer sending an incoming message — does NOT touch real WhatsApp.
            </div>
            <div className="flex gap-2">
              <Input value={testInput} onChange={e=>setTestInput(e.target.value)} placeholder="Type incoming message…" className="rounded-sm text-xs" data-testid="test-input" />
              <Button onClick={doTest} className="rounded-sm gap-1" data-testid="test-send">
                <Send className="h-3.5 w-3.5" /> Send
              </Button>
            </div>
            <div className="border rounded-sm bg-[#efeae2] dark:bg-slate-900 p-3 h-64 overflow-y-auto space-y-1.5" data-testid="test-messages">
              {testMsgs.length === 0 ? (
                <div className="text-xs text-muted-foreground text-center pt-16">
                  Bot replies will appear here…
                </div>
              ) : (
                testMsgs.map((m, i) => (
                  <div key={i} className="flex justify-end">
                    <div className="bg-green-100 dark:bg-green-950/40 border rounded p-2 max-w-[80%] text-xs">
                      <div>{m.body}</div>
                      {m.interactive?.action?.buttons && (
                        <div className="mt-1.5 space-y-0.5">
                          {m.interactive.action.buttons.map((b, j) => (
                            <div key={j} className="border rounded px-2 py-1 text-[11px] bg-background/80">
                              {b.reply.title}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setTestOpen(false)} className="rounded-sm">Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// ─────────────────── Defaults for each node type ───────────────────
function defaultsFor(type) {
  switch (type) {
    case "trigger":         return { trigger_type: "exact_keyword", keywords: [] };
    case "send_text":       return { text: "Hi {{contact.name}}, how may I help you?" };
    case "send_buttons":    return { body_text: "Choose an option:", buttons: [
      { id: "btn_" + Math.random().toString(36).slice(2, 6), title: "Option 1" }
    ]};
    case "ask_question":    return { question: "What is your name?", response_variable: "answer" };
    case "condition":       return { variable: "answer", operator: "equals", value: "" };
    case "assign_tag":      return { tag: "" };
    case "remove_tag":      return { tag: "" };
    case "update_contact":  return { updates: {} };
    case "delay":           return { seconds: 2 };
    case "human_handover":  return { message: "Connecting you to our team…", department: "" };
    case "goto_main":       return {};
    case "end_chat":        return { text: "Thank you! Have a great day." };
    default: return {};
  }
}

// ─────────────────── Node properties panel ───────────────────
function NodeProperties({ node, onChange, onDelete }) {
  const t = node.data.type;
  const d = node.data.data || {};
  return (
    <div className="p-3 space-y-3">
      <div className="flex items-center justify-between">
        <div>
          <div className="text-[10px] uppercase tracking-wider text-muted-foreground">{nodeMeta(t).label}</div>
          <div className="text-xs font-mono truncate" data-testid="prop-node-id">ID: {node.id}</div>
        </div>
        {t !== "trigger" && (
          <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={onDelete} data-testid="prop-delete-node">
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        )}
      </div>
      <Separator />

      {t === "trigger" && (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Trigger type</Label>
            <select value={d.trigger_type || "any_message"} onChange={e => onChange({ trigger_type: e.target.value })}
              className="w-full h-8 rounded-sm border bg-background text-xs px-2 mt-1" data-testid="prop-trigger-type">
              <option value="any_message">Any incoming message</option>
              <option value="first_message">First incoming message</option>
              <option value="exact_keyword">Exact keyword</option>
              <option value="contains_keyword">Contains keyword</option>
              <option value="starts_with">Starts with keyword</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Keywords (comma-separated)</Label>
            <Input value={(d.keywords || []).join(", ")}
              onChange={e => onChange({ keywords: e.target.value.split(",").map(s=>s.trim()).filter(Boolean) })}
              className="rounded-sm h-8 text-xs mt-1" data-testid="prop-trigger-keywords" />
          </div>
        </>
      )}

      {t === "send_text" && (
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Message</Label>
          <Textarea rows={5} value={d.text || ""} onChange={e => onChange({ text: e.target.value })}
            className="rounded-sm text-xs mt-1" data-testid="prop-text-body" />
          <VariablePicker />
        </div>
      )}

      {t === "send_buttons" && (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Body text</Label>
            <Textarea rows={3} value={d.body_text || ""} onChange={e => onChange({ body_text: e.target.value })}
              className="rounded-sm text-xs mt-1" data-testid="prop-buttons-body" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Header (optional)</Label>
            <Input value={d.header_text || ""} onChange={e => onChange({ header_text: e.target.value })}
              className="rounded-sm h-8 text-xs mt-1" data-testid="prop-buttons-header" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Footer (optional)</Label>
            <Input value={d.footer_text || ""} onChange={e => onChange({ footer_text: e.target.value })}
              className="rounded-sm h-8 text-xs mt-1" data-testid="prop-buttons-footer" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Buttons ({(d.buttons || []).length}/3)</Label>
            <div className="space-y-1 mt-1">
              {(d.buttons || []).map((b, i) => (
                <div key={b.id || i} className="flex gap-1 items-center">
                  <Input value={b.title || ""} onChange={e => {
                    const bs = [...(d.buttons || [])];
                    bs[i] = { ...bs[i], title: e.target.value.slice(0, 20) };
                    onChange({ buttons: bs });
                  }} className="rounded-sm h-8 text-xs" maxLength={20} data-testid={`prop-btn-title-${i}`} />
                  <Button variant="ghost" size="icon" className="h-7 w-7 text-red-600" onClick={() => {
                    onChange({ buttons: (d.buttons || []).filter((_, j) => j !== i) });
                  }} data-testid={`prop-btn-remove-${i}`}>
                    <X className="h-3.5 w-3.5" />
                  </Button>
                </div>
              ))}
              {(d.buttons || []).length < 3 && (
                <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs gap-1 w-full" onClick={() => {
                  const bs = [...(d.buttons || []), { id: "btn_" + Math.random().toString(36).slice(2, 6), title: `Option ${(d.buttons || []).length + 1}` }];
                  onChange({ buttons: bs });
                }} data-testid="prop-btn-add">
                  <Plus className="h-3 w-3" /> Add button
                </Button>
              )}
            </div>
            <div className="text-[10px] text-muted-foreground mt-1">
              WhatsApp limits button titles to 20 characters. Each button becomes its own connector.
            </div>
          </div>
        </>
      )}

      {t === "ask_question" && (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Question</Label>
            <Textarea rows={3} value={d.question || ""} onChange={e => onChange({ question: e.target.value })}
              className="rounded-sm text-xs mt-1" data-testid="prop-ask-question" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Save reply in variable</Label>
            <Input value={d.response_variable || ""} onChange={e => onChange({ response_variable: e.target.value.replace(/\W/g, "_") })}
              placeholder="answer" className="rounded-sm h-8 text-xs mt-1 font-mono" data-testid="prop-ask-var" />
            <div className="text-[10px] text-muted-foreground mt-1">
              Access later as <code>{"{{custom." + (d.response_variable || "answer") + "}}"}</code>
            </div>
          </div>
        </>
      )}

      {t === "condition" && (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Variable</Label>
            <Input value={d.variable || ""} onChange={e => onChange({ variable: e.target.value })}
              placeholder="answer" className="rounded-sm h-8 text-xs mt-1 font-mono" data-testid="prop-cond-var" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Operator</Label>
            <select value={d.operator || "equals"} onChange={e => onChange({ operator: e.target.value })}
              className="w-full h-8 rounded-sm border bg-background text-xs px-2 mt-1" data-testid="prop-cond-op">
              {["equals","not_equals","contains","not_contains","starts_with","ends_with","greater_than","less_than","exists","empty"].map(op => (
                <option key={op} value={op}>{op.replace(/_/g," ")}</option>
              ))}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Compare to</Label>
            <Input value={d.value || ""} onChange={e => onChange({ value: e.target.value })}
              className="rounded-sm h-8 text-xs mt-1" data-testid="prop-cond-value" />
          </div>
        </>
      )}

      {(t === "assign_tag" || t === "remove_tag") && (
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Tag name</Label>
          <Input value={d.tag || ""} onChange={e => onChange({ tag: e.target.value })}
            placeholder="Coupon Request" className="rounded-sm h-8 text-xs mt-1" data-testid="prop-tag-name" />
        </div>
      )}

      {t === "update_contact" && (
        <UpdateContactEditor updates={d.updates || {}} onChange={updates => onChange({ updates })} />
      )}

      {t === "delay" && (
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Delay (seconds)</Label>
          <Input type="number" min="0" max="60" value={d.seconds ?? 2}
            onChange={e => onChange({ seconds: Math.max(0, Number(e.target.value) || 0) })}
            className="rounded-sm h-8 text-xs mt-1" data-testid="prop-delay-seconds" />
          <div className="text-[10px] text-muted-foreground mt-1">
            Max 5 seconds in-flight; longer delays queued in background (Phase 2).
          </div>
        </div>
      )}

      {t === "human_handover" && (
        <>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Handover message</Label>
            <Textarea rows={3} value={d.message || ""} onChange={e => onChange({ message: e.target.value })}
              className="rounded-sm text-xs mt-1" data-testid="prop-handover-msg" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Department (optional)</Label>
            <Input value={d.department || ""} onChange={e => onChange({ department: e.target.value })}
              placeholder="sales | support | billing" className="rounded-sm h-8 text-xs mt-1" data-testid="prop-handover-dept" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-wider">Assign to agent (email, optional)</Label>
            <Input value={d.agent_email || ""} onChange={e => onChange({ agent_email: e.target.value })}
              className="rounded-sm h-8 text-xs mt-1" data-testid="prop-handover-agent" />
          </div>
        </>
      )}

      {t === "end_chat" && (
        <div>
          <Label className="text-[10px] uppercase tracking-wider">Final message</Label>
          <Textarea rows={3} value={d.text || ""} onChange={e => onChange({ text: e.target.value })}
            className="rounded-sm text-xs mt-1" data-testid="prop-end-text" />
        </div>
      )}
    </div>
  );
}

function UpdateContactEditor({ updates, onChange }) {
  const entries = Object.entries(updates);
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-wider">Updates</Label>
      <div className="space-y-1 mt-1">
        {entries.map(([field, value], i) => (
          <div key={i} className="flex gap-1">
            <Input value={field} onChange={e => {
              const nu = { ...updates };
              delete nu[field]; nu[e.target.value] = value;
              onChange(nu);
            }} placeholder="field" className="rounded-sm h-8 text-xs font-mono w-1/2" />
            <Input value={value} onChange={e => {
              onChange({ ...updates, [field]: e.target.value });
            }} placeholder="{{custom.answer}}" className="rounded-sm h-8 text-xs" />
          </div>
        ))}
        <Button variant="outline" size="sm" className="rounded-sm h-7 text-xs w-full" onClick={() => {
          onChange({ ...updates, ["field" + (entries.length + 1)]: "" });
        }}>+ Add field</Button>
      </div>
      <div className="text-[10px] text-muted-foreground mt-1">
        Built-in: <code>name</code>, <code>email</code>. Any other name = custom field.
      </div>
    </div>
  );
}

function VariablePicker() {
  const vars = ["{{contact.name}}", "{{contact.phone}}", "{{contact.email}}", "{{tenant.name}}", "{{current_date}}", "{{current_time}}"];
  return (
    <div className="mt-1.5">
      <div className="text-[10px] uppercase tracking-wider text-muted-foreground mb-1">Variables — click to copy</div>
      <div className="flex flex-wrap gap-1">
        {vars.map(v => (
          <button key={v} onClick={() => { navigator.clipboard.writeText(v); toast.success(`Copied ${v}`); }}
            className="text-[10px] px-1.5 py-0.5 rounded-sm border bg-background hover:bg-muted font-mono">
            {v}
          </button>
        ))}
      </div>
    </div>
  );
}
